import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import getAmplifyConfig from './utils/configLoader';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import SetPasswordPage from './pages/SetPasswordPage';
import ChatPage from './pages/ChatPage';
import OrgChatPage from './pages/OrgChatPage';
import ManageStaffPage from './pages/ManageStaffPage';
import ProcedureCatalogPage from './pages/ProcedureCatalogPage';
import DepartmentSettings from './components/settings/DepartmentSettings';
import LabCatalogPage from './pages/LabCatalogPage';
import BedCatalogPage from './pages/BedCatalogPage';
import SurgeryCatalogPage from './pages/SurgeryCatalogPage';
import OTRoomCatalogPage from './pages/OTRoomCatalogPage';
import ChargeItemsPage from './pages/ChargeItemsPage';
import BedSetupPage from './pages/BedSetupPage';
import BedBoardPage from './pages/BedBoardPage';
import AdmissionEnabledGate from './components/AdmissionEnabledGate';
import AdmissionDeskPage from './components/admission/AdmissionDeskPage';
import AdmitPatientPage from './components/admission/AdmitPatientPage';
import OccupancyBoardPage from './pages/OccupancyBoardPage';
import IpdPage from './pages/IpdPage';
import MonthlyCollectionsPage from './pages/MonthlyCollectionsPage';
import BusyImportGuidePage from './pages/BusyImportGuidePage';
import LabQueuePage from './pages/LabQueuePage';
import DoctorRateOverridesPage from './pages/DoctorRateOverridesPage';
import FeesBillingPage from './pages/FeesBillingPage';
import SettingsPage from './pages/SettingsPage';
import ProcedureQueuePage from './pages/ProcedureQueuePage';
import ActionPendingPage from './pages/ActionPendingPage';
import TreatmentPlansPage from './pages/TreatmentPlansPage';
import PatientPlanPage from './pages/PatientPlanPage';
import PatientPlansIndexPage from './pages/PatientPlansIndexPage';
import ABHAPage from './pages/ABHAPage';
import HiuConsentRequestPage from './pages/HiuConsentRequestPage';
import HiuConsentsListPage from './pages/HiuConsentsListPage';
import HiuConsentDetailPage from './pages/HiuConsentDetailPage';
import HiuConsentRecordsPage from './pages/HiuConsentRecordsPage';
import HipCareContextPage from './pages/HipCareContextPage';
import HipPatientRecordsPage from './pages/HipPatientRecordsPage';
import PatientDirectoryPage from './pages/PatientDirectoryPage';
import PatientCardPage from './pages/PatientCardPage';
import AdminRoleGate from './components/AdminRoleGate';
import StaffOnboardingPage from './pages/StaffOnboardingPage';
import AccessDenied from './pages/AccessDenied';
import PublicQueuePage from './pages/PublicQueuePage';
import DoctorProfilePage from './pages/DoctorProfilePage';
import PracticePrescriptionPage from './pages/PracticePrescriptionPage';
import ReviewPage from './pages/ReviewPage';
import AboutPage from './pages/AboutPage';
import ProtectedRoute from './components/ProtectedRoute';
import { ThemeProvider } from './theme/ThemeContext';
import { AgentProvider } from './context/AgentContext';
import { ConversationProvider } from './context/ConversationContext';
import { AuthProvider } from './context/AuthContext';
import { ROUTES, LEGACY_CROW_ROUTES } from './constants/routes';
import { STORAGE_KEYS } from './constants';
import { isTokenExpired, getSession, storeSession } from './services/api/sessionApi';
import { installSyncDebugger } from './services/SyncDebugger';
import { PermissionProvider, usePermission } from './context/PermissionContext';
import useLabEnabled from './hooks/useLabEnabled';
import FileViewerModal from './components/common/FileViewerModal/FileViewerModal';
import { ActiveWidgetOverlay } from './widgets';
// Inline the anatomical tooth sprite once at app root so the
// <use href="#tooth-…"> references inside ArchOdontogram resolve
// same-document. SVGR's ReactComponent import (built into CRA) returns
// the SVG as an inlined React component instead of a bundled URL —
// required because cross-document <use href="url.svg#id"> silently
// fails under CRA's webpack 5 asset pipeline.
import { ReactComponent as ToothAnatomySprite } from './assets/dental/tooth-anatomy.svg';

import './App.css';

// Install runtime debug instrumentation (no-op when REACT_APP_DEBUG !== 'true')
installSyncDebugger();

/**
 * AdminOnlyGate — Route guard for features under development.
 * Renders children only if user has FUTURE_DEVELOPMENT permission (Admin only).
 * Wrap any Coming Soon / WIP route with this to hide it from non-admin roles.
 */
function AdminOnlyGate({ children }) {
  const canAccess = usePermission('FUTURE_DEVELOPMENT');
  if (!canAccess) return <Navigate to={ROUTES.ORG} replace />;
  return children;
}

/**
 * LabEnabledGate — gates lab routes behind the per-org enableLabModule flag.
 * Redirects to the org home when the Lab module is off, so a lab-disabled org
 * behaves exactly as today.
 */
function LabEnabledGate({ children }) {
  const labOn = useLabEnabled();
  if (!labOn) return <Navigate to={ROUTES.ORG} replace />;
  return children;
}

/**
 * HiuConsentRequestGate / HiuViewRecordsGate — M3 HIU permission gates.
 * Gated by permission slug (admin-configurable per role). Request gates the consent-request page and the
 * write actions inside the detail page; ViewRecords gates the read-only list/detail/viewer
 * surfaces.
 */
function HiuConsentRequestGate({ children }) {
  const canAccess = usePermission('HIU_CONSENT_REQUEST');
  if (!canAccess) return <Navigate to={ROUTES.ORG} replace />;
  return children;
}
function HiuViewRecordsGate({ children }) {
  const canAccess = usePermission('HIU_VIEW_RECORDS');
  if (!canAccess) return <Navigate to={ROUTES.ORG} replace />;
  return children;
}

/**
 * HipCareContextGate — M2 HIP permission gate (HIP_CARE_CONTEXT; this pushes clinical
 * visit content outward to ABDM). Independent
 * of the Patient Card epic's own admin-only wall — deliberately not reused, since a
 * DOCTOR needs to reach this page but not that one.
 */
function HipCareContextGate({ children }) {
  const canAccess = usePermission('HIP_CARE_CONTEXT');
  if (!canAccess) return <Navigate to={ROUTES.ORG} replace />;
  return children;
}

/**
 * AdmissionEnabledGate — the Admission (IPD) twin of LabEnabledGate. Gates
 * future IPD routes behind the per-org enableAdmissionModule flag (redirects
 * to the org home when off). B1 wraps NO routes yet — no IPD screen exists
 * until B2. Lives in its own module (components/AdmissionEnabledGate) so it
 * is unit-testable; re-exported here beside LabEnabledGate for B2 wiring.
 */
export { default as AdmissionEnabledGate } from './components/AdmissionEnabledGate';

// Load environment-specific Amplify configuration
const amplifyConfig = getAmplifyConfig();

// Configure Amplify with Auth-only (no credentials provider for unauthenticated users)
// This prevents the "Unauthenticated access is not supported" error
Amplify.configure({
  ...amplifyConfig,
  // Disable credential fetching for unauthenticated users
  Auth: {
    Cognito: {
      userPoolId: amplifyConfig.aws_user_pools_id,
      userPoolClientId: amplifyConfig.aws_user_pools_web_client_id,
      region: amplifyConfig.aws_cognito_region,
    }
  }
});

/**
 * Get the route for the user's assigned agent
 * @returns {string} - Route path for the user's agent page
 */
function getAgentRoute() {
  const agentType = localStorage.getItem('agentType');
  
  if (agentType === 'brochat') {
    return ROUTES.CHAT;
  } else if (agentType === 'crowchat') {
    return ROUTES.ORG;
  }
  
  // Default to chat if agent not found
  return ROUTES.CHAT;
}

/**
 * Smart Home Route Component
 * - If authenticated: redirect to user's agent page
 * - If not authenticated: show login page
 */
function HomeRoute() {
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      try {
        // If this tab was redirected due to auth failure, skip re-checking
        // (prevents loop when localStorage still has stale session metadata)
        const authFailed = sessionStorage.getItem('__ak_auth_failed');
        if (authFailed) {
          sessionStorage.removeItem('__ak_auth_failed');
          if (!cancelled) setIsAuthenticated(false);
          return;
        }

        // Fast check: is there stored session metadata?
        const agentType = localStorage.getItem(STORAGE_KEYS.AGENT_TYPE);
        const tokenExpiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);

        if (!agentType || !tokenExpiry) {
          if (!cancelled) setIsAuthenticated(false);
          return;
        }

        // Refresh if the stored expiry says expired OR if this tab has
        // no in-memory token (e.g. new tab — localStorage has metadata but
        // the module-level _inMemoryToken may not have been populated yet).
        const { getAuthToken, requestTokenRefresh } = await import('./services/api/apiClient');
        if (isTokenExpired() || !getAuthToken()) {
          try {
            const refreshed = await requestTokenRefresh();
            if (!refreshed) {
              if (!cancelled) setIsAuthenticated(false);
              return;
            }
            // Refresh succeeded — token is fresh, continue with backend check
          } catch {
            // Refresh failed — session is truly dead
            if (!cancelled) setIsAuthenticated(false);
            return;
          }
        }

        // Authoritative check: call backend /api/session
        // The httpOnly JWT cookie is sent automatically by the browser
        try {
          const sessionData = await getSession();
          if (!cancelled) {
            // Refresh stored session from authoritative source
            storeSession(sessionData);
            // Schedule proactive token refresh for this session
            import('./services/api/apiClient').then(({ scheduleTokenRefresh }) => {
              scheduleTokenRefresh();
            }).catch(() => {});
            setIsAuthenticated(true);
          }
        } catch (backendError) {
          const isDeviceDenied =
            backendError.message === 'DEVICE_NOT_APPROVED' || backendError.message === 'DEVICE_ID_MISSING';
          if (isDeviceDenied) {
            // RC-2: Cognito already minted tokens for this browser session —
            // revoke them so a denied device never keeps a working session.
            const { signOut } = await import('./services/auth/authApi');
            await signOut();
          }
          if (isDeviceDenied || backendError.message === 'UNAUTHORIZED' || backendError.message === 'FORBIDDEN' || backendError.message === 'ACCOUNT_DISABLED') {
            if (!cancelled) setIsAuthenticated(false);
          } else {
            // Network error — only trust local session if token is NOT expired.
            // If token is expired + backend is unreachable, don't resurrect a dead session.
            if (isTokenExpired()) {
              console.warn('[HomeRoute] Backend unreachable + token expired — not authenticated');
              if (!cancelled) setIsAuthenticated(false);
            } else {
              console.warn('[HomeRoute] Backend unreachable, trusting local session (token still valid)');
              if (!cancelled) setIsAuthenticated(true);
            }
          }
        }
      } catch (error) {
        if (!cancelled) setIsAuthenticated(false);
      }
    };

    checkAuth();

    return () => { cancelled = true; };
  }, []);

  // Loading state
  if (isAuthenticated === null) {
    return <p>Loading...</p>;
  }

  // Authenticated: redirect to agent page
  if (isAuthenticated) {
    return <Navigate to={getAgentRoute()} replace />;
  }

  // Not authenticated: show login
  return <LoginPage />;
}

function App() {
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    // Quick initialization check
    setInitializing(false);
  }, []);

  if (initializing) return <p>Loading...</p>;

  return (
    <ErrorBoundary>
    <ToothAnatomySprite
      aria-hidden="true"
      focusable="false"
      style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
    />
    <ThemeProvider>
      <AuthProvider>
      <AgentProvider>
        <ConversationProvider>
          <Router>
            <PermissionProvider>
            <div className="App">
            <main className="App-header">
              <Routes>
                {/* Smart home route - redirects based on auth state */}
                <Route path={ROUTES.HOME} element={<HomeRoute />} />
                
                {/* Auth routes */}
                <Route path={ROUTES.LOGIN} element={<HomeRoute />} />
                <Route path={ROUTES.SIGNUP} element={<SignupPage />} />
                <Route path={ROUTES.SET_PASSWORD} element={<SetPasswordPage />} />
                <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
                
                {/* Protected agent routes */}
                <Route
                  path={ROUTES.CHAT}
                  element={
                    <ProtectedRoute requiredAgent="brochat">
                      <ChatPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.ORG}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <OrgChatPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.MANAGE_STAFF}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <ManageStaffPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.STAFF_ONBOARDING}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <StaffOnboardingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.FEES_BILLING}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <FeesBillingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.SETTINGS}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.PROCEDURES}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <ProcedureCatalogPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.DEPARTMENTS}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <DepartmentSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.LAB_CATALOG}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <LabEnabledGate>
                        <LabCatalogPage />
                      </LabEnabledGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.LAB_QUEUE}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <LabEnabledGate>
                        <LabQueuePage />
                      </LabEnabledGate>
                    </ProtectedRoute>
                  }
                />
                {/* B2 — IPD config catalogs, each behind the admission gate */}
                <Route
                  path={ROUTES.BED_CATEGORIES}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <AdmissionEnabledGate>
                        <BedCatalogPage />
                      </AdmissionEnabledGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.SURGERY_CATALOG}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <AdmissionEnabledGate>
                        <SurgeryCatalogPage />
                      </AdmissionEnabledGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.OT_ROOMS}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <AdmissionEnabledGate>
                        <OTRoomCatalogPage />
                      </AdmissionEnabledGate>
                    </ProtectedRoute>
                  }
                />
                {/* Day-1 IPD / S1 — Charge Items master, behind the admission gate */}
                <Route
                  path={ROUTES.CHARGE_ITEMS}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <AdmissionEnabledGate>
                        <ChargeItemsPage />
                      </AdmissionEnabledGate>
                    </ProtectedRoute>
                  }
                />
                {/* B3 — ward→room→bed inventory builder + read-only bed-board preview */}
                <Route
                  path={ROUTES.BED_SETUP}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <AdmissionEnabledGate>
                        <BedSetupPage />
                      </AdmissionEnabledGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.BED_BOARD}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <AdmissionEnabledGate>
                        <BedBoardPage />
                      </AdmissionEnabledGate>
                    </ProtectedRoute>
                  }
                />
                {/* C1 — Admit + assign bed, behind the admission gate */}
                <Route
                  path={ROUTES.ADMIT_PATIENT}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <AdmissionEnabledGate>
                        <AdmitPatientPage />
                      </AdmissionEnabledGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.ADMISSIONS}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <AdmissionEnabledGate>
                        <AdmissionDeskPage />
                      </AdmissionEnabledGate>
                    </ProtectedRoute>
                  }
                />
                {/* C2 — Live occupancy board, behind the admission gate */}
                <Route
                  path={ROUTES.OCCUPANCY_BOARD}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <AdmissionEnabledGate>
                        <OccupancyBoardPage />
                      </AdmissionEnabledGate>
                    </ProtectedRoute>
                  }
                />
                {/* C6b — the IPD tile/page, behind the admission gate.
                    ACCESS_IPD (C7) is the page's own in-page gate. */}
                <Route
                  path={ROUTES.IPD}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <AdmissionEnabledGate>
                        <IpdPage />
                      </AdmissionEnabledGate>
                    </ProtectedRoute>
                  }
                />
                {/* S2 — Collections board. Permission-gated ONLY
                    (VIEW_MONTHLY_COLLECTIONS) — NOT behind
                    AdmissionEnabledGate, so an OPD-only org can reach it. */}
                <Route
                  path={ROUTES.MONTHLY_COLLECTIONS}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <MonthlyCollectionsPage />
                    </ProtectedRoute>
                  }
                />
                {/* BUSY-VOUCHER R3 — "How to import into Busy" guide. The page
                    checks DOWNLOAD_BUSY_FILES itself (same as the
                    Monthly files section); not admission gated. */}
                <Route
                  path={ROUTES.BUSY_IMPORT_GUIDE}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <BusyImportGuidePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.DOCTOR_RATES}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <DoctorRateOverridesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.PROCEDURE_QUEUE}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <ProcedureQueuePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.ACTION_PENDING}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <ActionPendingPage />
                    </ProtectedRoute>
                  }
                />
                {/* Phase 2.7 — workflow plan authoring (doctor's reusable plan library) */}
                <Route
                  path={ROUTES.TREATMENT_PLANS}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <TreatmentPlansPage />
                    </ProtectedRoute>
                  }
                />
                {/* Phase 2.8 — patient plans (clinic-wide list + detail) */}
                <Route
                  path={ROUTES.PATIENT_PLANS}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <PatientPlansIndexPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.PATIENT_PLAN_DETAIL}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <PatientPlanPage />
                    </ProtectedRoute>
                  }
                />
                {/* M1 — ABHA health ID management */}
                <Route
                  path={ROUTES.ABHA}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <ABHAPage />
                    </ProtectedRoute>
                  }
                />
                {/* M3 — ABDM HIU (external patient history under consent) */}
                <Route
                  path={ROUTES.HIU_CONSENT_REQUEST}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <HiuConsentRequestGate>
                        <HiuConsentRequestPage />
                      </HiuConsentRequestGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.HIU_CONSENTS}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <HiuViewRecordsGate>
                        <HiuConsentsListPage />
                      </HiuViewRecordsGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.HIU_CONSENT_DETAIL}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <HiuViewRecordsGate>
                        <HiuConsentDetailPage />
                      </HiuViewRecordsGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.HIU_CONSENT_RECORDS}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <HiuViewRecordsGate>
                        <HiuConsentRecordsPage />
                      </HiuViewRecordsGate>
                    </ProtectedRoute>
                  }
                />
                {/* M2 — ABDM HIP (publish this clinic's own visit records outward) */}
                <Route
                  path={ROUTES.HIP_CARE_CONTEXTS}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <HipCareContextGate>
                        <HipCareContextPage />
                      </HipCareContextGate>
                    </ProtectedRoute>
                  }
                />
                {/* OR-75 — every visit and admission of ONE patient (12 months); same gate + permission */}
                <Route
                  path={ROUTES.HIP_PATIENT_RECORDS}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <HipCareContextGate>
                        <HipPatientRecordsPage />
                      </HipCareContextGate>
                    </ProtectedRoute>
                  }
                />

                {/* EPIC PATIENT-CARD — patient directory + 360° patient card,
                    admin only. AdminRoleGate gates on the ROLE, not on a
                    permission slug (D7), and is UX only — every card endpoint
                    enforces ADMIN itself, so the API is the wall. The
                    parameterised card path is less specific than
                    ROUTES.CREATE_TREATMENT_PLAN and so cannot shadow it
                    (react-router ranks by specificity, not declaration
                    order). */}
                <Route
                  path={ROUTES.PATIENT_DIRECTORY}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <AdminRoleGate>
                        <PatientDirectoryPage />
                      </AdminRoleGate>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={ROUTES.PATIENT_CARD}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <AdminRoleGate>
                        <PatientCardPage />
                      </AdminRoleGate>
                    </ProtectedRoute>
                  }
                />

                {/* REVIEW-FORM / R1 — in-app "Rate ArogyaKhata" (D11: same
                    guard as every /org* sibling — crowchat, no permission
                    slug, every role may submit). */}
                <Route
                  path={ROUTES.REVIEW}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <ReviewPage />
                    </ProtectedRoute>
                  }
                />

                {/* OR-138 — About (data-source attribution): same guard as Review, every role. */}
                <Route
                  path={ROUTES.ABOUT}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <AboutPage />
                    </ProtectedRoute>
                  }
                />


                {/* Public queue — Admin only (Coming Soon) */}
                <Route
                  path={ROUTES.PUBLIC_QUEUE}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <AdminOnlyGate><PublicQueuePage /></AdminOnlyGate>
                    </ProtectedRoute>
                  }
                />

                {/* Doctor public profile — Admin only (Coming Soon) */}
                <Route
                  path={ROUTES.DOCTOR_PROFILE}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <AdminOnlyGate><DoctorProfilePage /></AdminOnlyGate>
                    </ProtectedRoute>
                  }
                />

                {/* Prescription Practice Tour — guided sandbox for new doctors */}
                <Route
                  path={ROUTES.PRACTICE_PRESCRIPTION}
                  element={
                    <ProtectedRoute requiredAgent="crowchat">
                      <PracticePrescriptionPage />
                    </ProtectedRoute>
                  }
                />

                {/* Access denied */}
                <Route
                  path={ROUTES.ACCESS_DENIED}
                  element={<AccessDenied />}
                />

                {/* Legacy /crow/* → /org/* redirects (non-breaking migration) */}
                {LEGACY_CROW_ROUTES.map(({ from, to }) => (
                  <Route key={from} path={from} element={<Navigate to={to} replace />} />
                ))}
              </Routes>
              <FileViewerModal />
              {/* Widget Library — root-level overlay so sdk.openWidget(...)
                  works from any page. WidgetHost (mounted inside
                  DoctorQueueView's active-visit branch) renders the
                  procedure rail plus its own active-widget switch for
                  the consultation context; this overlay catches the
                  active widget for every other page. */}
              <ActiveWidgetOverlay />
            </main>
          </div>
          </PermissionProvider>
        </Router>
        </ConversationProvider>
      </AgentProvider>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
